import {LayersSet} from "./LayersSet";
import {gsap} from "gsap";
import {SimpleSpinReelModel} from "../../core/reel/simplereel/SimpleSpinReelModel";
import {
    Positionable,
    SimpleReelModelDelegate,
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData,
    SimpleReelModelSymbolPosition
} from "../../core/reel/simplereel/SimpleReelModel";
import {BS2Statics} from "../config/BS2Statics";
import {BS2Symbol, BS2SymbolStates} from "./BS2Symbol";
import {ScatterSymbol} from "./ScatterSymbol";
import {BS2} from "../BS2";
import {XSplitSymbol} from "./XSplitSymbol";
import {WildSymbol} from "./WildSymbol";
import {BS2GameAssets} from "../BS2GameAssets";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";

export interface SymbolReplaceData {
    oldSymbolData: SimpleReelModelSymbolData;
    newSymbolData: SimpleReelModelSymbolData;
}

/**
 * Created by jonas on 2021-11-19.
 */

export class BS2Reel implements SimpleReelModelDelegate {

    public id: number;
    public model: SimpleSpinReelModel;
    public layers: LayersSet;
    public position: PIXI.ObservablePoint;

    private _pivot: PIXI.ObservablePoint;
    private _scale: PIXI.ObservablePoint;
    private currentReelSet: string[][];
    private spinAnimation: gsap.core.Timeline;

    constructor(id: number) {
        this.position = new PIXI.ObservablePoint(this.onPosition, this);
        this._pivot = new PIXI.ObservablePoint(this.onPivot, this);
        this._scale = new PIXI.ObservablePoint(this.onScale, this);

        this.id = id;

        this.model = new SimpleSpinReelModel(id, this, true);
        this.model.illegalVisualPaddingSymbols = [BS2Statics.SYMBOLS.S];
        this.model.setReelSet(BS2Statics.DEFAULT_REEL_SET);
        this.model.stepDuration = BS2Statics.stepDuration;
        this.model.stepSize = BS2Statics.stepSize;
        this.model.events?.on(SimpleReelModelState.BOUNCE, (data: SimpleReelModelEventData) => {
            //Switches state of all symbols from spin state to normal, when the symbols bounce.
            const allSymbols:SimpleReelModelSymbolData[] = this.model.symbols;
            for (let symbolData of allSymbols) {
                (<BS2Symbol>symbolData.positionable).getStateAnimation(BS2SymbolStates.NORMAL);
                (<BS2Symbol>symbolData.positionable).alpha=1;
            }
            const allVisibleSymbols:SimpleReelModelSymbolData[] = this.model.getAllVisibleSymbols();
            for (let symbolData of allVisibleSymbols) {
                if (BS2.reelView.stickySymbols.matrix[symbolData.reelId][symbolData.reelPosition] === null) {
                    (<BS2Symbol>symbolData.positionable).getStateAnimation(BS2SymbolStates.LANDING);
                }
            }

        });
        this.model.events?.on(SimpleReelModelState.SPIN, (data: SimpleReelModelEventData) => {
            //Switches state of all symbols from whatever state to spin when reel wind up is done and the spinning starts.
            for (let symbolData of this.model.symbols) {
                (<BS2Symbol>symbolData.positionable).getStateAnimation(BS2SymbolStates.SPIN);
            }
        });

        this.layers = new LayersSet(`reel_${this.id}_`);
    }

    public init(reelData: string[], reelHeight: number, isAvalancheRestore: boolean) {
        this.model.paddingConfig.visualPaddingBefore =isAvalancheRestore?1: 0;
        this.model.paddingConfig.paddingBefore = isAvalancheRestore?0:1;
        this.model.paddingConfig.visibleSymbols = reelHeight;
        this.model.paddingConfig.paddingAfter =isAvalancheRestore?0: 1;
        this.model.paddingConfig.visualPaddingAfter =isAvalancheRestore?1: 0;
        this.model.init(reelData);
        if(isAvalancheRestore){
            this.updatePaddingData(5);
        }
    }

    public updatePaddingData(reelHeight: number) {
        this.model.updatePaddingConfig({
            visualPaddingBefore: 0,
            paddingBefore: 1,
            visibleSymbols: reelHeight,
            paddingAfter: 1,
            visualPaddingAfter: 0
        });
    }

    public start() {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this.layers.maskedNormal, {duration: 1, alpha: 0.5, ease: "power3.out"}));
        this.spinAnimation = tl;
    }

    createSymbol(symbolName: string, state: SimpleReelModelState, symbolPosition: SimpleReelModelSymbolPosition): Positionable {
        const symbolState: BS2SymbolStates = (state == SimpleReelModelState.SPIN || state == SimpleReelModelState.STOP) ? BS2SymbolStates.SPIN : BS2SymbolStates.NORMAL;
        let symbol;
        if (symbolName == BS2Statics.SYMBOLS.X) {
            symbol = new BS2Symbol(symbolName, BS2SymbolStates.REMOVE, this, symbolPosition);
        } else if (symbolName == BS2Statics.SYMBOLS.S) {
            symbol = new ScatterSymbol(symbolName, symbolState, this, symbolPosition);
        } else if (symbolName == BS2Statics.SYMBOLS.W) {
            symbol = new WildSymbol(symbolName, symbolState, this, symbolPosition);
        } else if (symbolName == BS2Statics.SYMBOLS.Y) {
            symbol = new WildSymbol(symbolName, symbolState, this, symbolPosition);
        } else if(symbolName===BS2Statics.SYMBOLS.D || symbolName===BS2Statics.SYMBOLS.G){
            symbol= new XSplitSymbol(symbolName, symbolState, this, symbolPosition);
        } else {
            symbol = new BS2Symbol(symbolName, symbolState, this, symbolPosition);
        }
        this.layers.maskedNormal.addChild(symbol);

        return symbol;
    }

    removeSymbol(symbolData: SimpleReelModelSymbolData): void {
        (<BS2Symbol>symbolData.positionable).destroy();
    }

    public getShowNearWin(loopCount: number = 4): gsap.core.Timeline {
        const tl = gsap.timeline();
        const spine = BS2.graphicsPool.getSpinePool(BS2GameAssets.NEAR_WIN_SPINE).withdraw();
        spine.position.y = BS2Statics.SYMBOL_HEIGHT * 1.5;

        tl.add(() => {
            this.layers.overlay.addChild(spine);
        });
        tl.add(SpineGSAPWrapper.createTimeline(spine, 0, "nearWin", loopCount));
        tl.add(() => {
            spine.state.setEmptyAnimation(0, 0);
            this.layers.overlay.removeChild(spine);
            BS2.graphicsPool.getSpinePool(BS2GameAssets.NEAR_WIN_SPINE).deposit(spine);
        })

        return tl;

    }

    public showNoNearWinOverlay(): gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        const sprite: PIXI.Sprite = new PIXI.Sprite(BS2.assetLoader.getTextures("reel_miss")[0]);
        sprite.blendMode = PIXI.BLEND_MODES.ADD;
        sprite.anchor.x = 0.5;
        sprite.y = 70;

        tl.add(() => {
            this.layers.overlay.addChild(sprite);
        });
        tl.add(gsap.to(sprite, {duration: 0.5, alpha: 0}), "<");
        tl.add(() => {
            this.layers.overlay.removeChild(sprite);
        });

        return tl;
    }

    private onPosition(): void {
        for (let layer of this.layers.all) {
            layer.position.set(this.position.x, this.position.y);
        }
    }

    private onPivot(): void {
        for (let layer of this.layers.all) {
            layer.pivot.set(this._pivot.x, this._pivot.y);
        }
    }

    private onScale() {
        for (let layer of this.layers.all) {
            layer.scale.set(this._scale.x, this._scale.y);
        }
    }

    private stop() {
        this.spinAnimation.kill();
        gsap.to(this.layers.maskedNormal, {duration: 0.1, alpha: 1});
    }


}